import { z } from 'zod';
import { type Money, type MoneyTotal, multiplyMoney, type RateLine, rateLines, sumMoney } from '../money';
import { toLocalDate } from '../time';
import type { ApprovalStateRef } from './approvals';
import { amountSchema, currencySchema, idSchema, localDateSchema, versionSchema } from './common';
import { companyAddressSchema } from './company-registry';

/**
 * Rozliczenia (docs/3VTOS_MVP.md §7 „Billing Draft”, Figma 09 Rozliczenia): `billing_drafts`, `billing_draft_items`
 * (packages/db/src/schema/billing.ts). Moduł uprawnień: `billing`; przygotowanie = flaga `preparesBilling`,
 * wystawienie = Approval z flagą `issuesInvoices` (wykonanie przez adapter faktur, fake = symulacja).
 *
 * Jedno źródło prawdy „wpis jest w zestawieniu”: `time_entries.billing_draft_id` (najwyżej jedno zestawienie,
 * ten sam klient — złożony FK). Pozycja `time` zestawienia wskazuje wpis; aktywna pozycja (bez `released_at`) jest
 * najwyżej jedna na wpis (unikalny indeks częściowy). Anulowanie zestawienia: pozycje dostają `released_at`,
 * a wpisy `billing_draft_id = null` (wracają do „niezafakturowanych”). Wystawienie: wpisy dostają `locked_at`.
 * Wejścia i wyjścia usług dopisuje agent modułu billing.
 */

export const BILLING_DRAFT_STATUSES = ['draft', 'pending_approval', 'issued', 'error', 'cancelled'] as const;
export type BillingDraftStatus = (typeof BILLING_DRAFT_STATUSES)[number];
export const billingDraftStatusSchema = z.enum(BILLING_DRAFT_STATUSES);

/** Zestawienia, które trzymają wpisy (wpis w takim zestawieniu nie trafi do innego). */
export const OPEN_BILLING_DRAFT_STATUSES = [
  'draft',
  'pending_approval',
  'error',
] as const satisfies readonly BillingDraftStatus[];

export const BILLING_ITEM_KINDS = ['time', 'fixed'] as const;
export type BillingItemKind = (typeof BILLING_ITEM_KINDS)[number];
export const billingItemKindSchema = z.enum(BILLING_ITEM_KINDS);

/** Skąd wiemy o opłaceniu: oznaczone ręcznie albo pobrane z integracji faktur. */
export const BILLING_PAYMENT_SOURCES = ['manual', 'integration'] as const;
export type BillingPaymentSource = (typeof BILLING_PAYMENT_SOURCES)[number];
export const billingPaymentSourceSchema = z.enum(BILLING_PAYMENT_SOURCES);

/** Pola pieniężne zestawienia i pozycji (bez „Widzi stawki i kwoty”: pominięte w DTO, CSV, PDF). */
export const BILLING_MONEY_FIELDS = ['totalNet', 'unitPrice', 'amount'] as const;

/** Ilość w pozycji stałej (numeric(12,2)), np. "1", "2.5". */
export const billingQuantitySchema = z
  .string()
  .trim()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, { message: 'Nieprawidłowa ilość' })
  .refine((v) => Number(v) > 0, { message: 'Ilość musi być dodatnia' });

/** Numer faktury z programu księgowego (opcjonalny, informacyjny; pusty = brak). */
export const billingInvoiceNumberSchema = z
  .string()
  .trim()
  .max(60, { message: 'Numer faktury: najwyżej 60 znaków' })
  .nullable()
  .optional()
  .transform((v) => (v === undefined ? undefined : v ? v : null));

const billingDraftFieldsBase = z.object({
  clientId: idSchema,
  /** okres zestawienia (włącznie), daty lokalne */
  periodFrom: localDateSchema,
  periodTo: localDateSchema,
  currency: currencySchema.default('PLN'),
  notes: z.string().trim().max(2000).nullable().optional(),
});

export const billingDraftPeriodRefinement = (v: { periodFrom?: string; periodTo?: string }, ctx: z.RefinementCtx) => {
  if (v.periodFrom && v.periodTo && v.periodTo < v.periodFrom) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['periodTo'], message: 'Koniec okresu przed początkiem' });
  }
};

export const billingDraftFieldsShape = billingDraftFieldsBase.shape;
export const billingDraftFieldsSchema = billingDraftFieldsBase.superRefine(billingDraftPeriodRefinement);
export type BillingDraftFields = z.infer<typeof billingDraftFieldsSchema>;

/** Pozycja stała (np. „Hosting 12 mies.”, „Konfiguracja domeny”): ilość × cena jednostkowa. */
export const billingFixedItemFieldsSchema = z.object({
  draftId: idSchema,
  description: z.string().trim().min(1, { message: 'Opis jest wymagany' }).max(300),
  quantity: billingQuantitySchema.default('1'),
  unitPrice: amountSchema,
});
export type BillingFixedItemFields = z.infer<typeof billingFixedItemFieldsSchema>;

/** Wybór wpisów do zestawienia (wpisy klienta zestawienia, rozliczane, niezablokowane, poza innym zestawieniem). */
export const billingSelectEntriesInput = z.object({
  draftId: idSchema,
  version: versionSchema,
  timeEntryIds: z
    .array(idSchema)
    .min(1, { message: 'Wybierz co najmniej jeden wpis' })
    .max(1000, { message: 'Najwyżej 1000 wpisów naraz' }),
});

/** Kwota pozycji stałej = ilość × cena (HALF_UP do grosza). */
export function computeFixedItemAmount(quantity: string, unitPrice: Money): Money {
  return multiplyMoney(unitPrice, quantity);
}

/** Pozycja zestawienia do przeliczenia sum (wiersz `billing_draft_items`). */
export interface BillingDraftItemForTotal {
  kind: BillingItemKind | string;
  amount: Money;
  /** stawka (pozycja `time`) albo cena jednostkowa (pozycja stała) */
  unitPrice?: Money | null;
  quantitySeconds?: number | null;
  releasedAt?: Date | null;
}

/**
 * Pozycje czasu wg stawki (DECISIONS 2026-10-05 [P], `rateLines` z @fos/shared/money): wpisy zestawienia o tej samej
 * stawce = jedna pozycja, kwota = round(stawka × łączny czas, 2). Czas bez stawki nie tworzy pozycji (0 zł).
 * Pozycje zwolnione (`releasedAt`) się nie liczą. Waluta = waluta zestawienia (wszystkie wpisy w tej samej).
 */
export function computeDraftRateLines(items: readonly BillingDraftItemForTotal[], currency = 'PLN'): RateLine[] {
  return rateLines(
    items
      .filter((i) => !i.releasedAt && i.kind === 'time')
      .map((i) => ({ rate: i.unitPrice ?? null, seconds: i.quantitySeconds ?? 0, currency })),
    currency,
  );
}

/**
 * Suma zestawienia = suma pozycji czasu wg stawki (`computeDraftRateLines`: stawka × łączny czas grupy, zaokrąglone
 * raz) + suma pozycji stałych. Kwoty pojedynczych wpisów (`amount` pozycji `time`) są informacyjne i nie sumują się
 * do zestawienia (3 × 1 h 40 min po 170 zł = 850,00 zł, nie 849,99 zł). Pozycje zwolnione się nie liczą.
 */
export function computeDraftTotal(items: readonly BillingDraftItemForTotal[]): Money {
  const active = items.filter((i) => !i.releasedAt);
  const time = computeDraftRateLines(active).map((l) => l.amount);
  const fixed = active.filter((i) => i.kind !== 'time').map((i) => i.amount);
  return sumMoney([...time, ...fixed]);
}

/** Komunikaty modułu Rozliczenia. */
/** Powód wycofania akceptacji `billing.issue_invoice` (Akceptacje: „Wycofana”). */
export const BILLING_WITHDRAW_REASONS = {
  reopen: 'Zestawienie wróciło do edycji',
  cancel: 'Zestawienie anulowane',
} as const;

export const BILLING_MESSAGES = {
  entryInOtherDraft: 'Wpis jest już w innym zestawieniu',
  entryLocked: 'Wpis jest zamknięty w wystawionym rozliczeniu',
  entryOtherClient: 'Wpis należy do innego klienta',
  entryNotBillable: 'Wpis nierozliczany nie trafia do zestawienia',
  draftNotEditable: 'Zestawienie czeka na akceptację albo jest wystawione — nie można go zmieniać',
  issuedNotCancellable: 'Wystawionego rozliczenia nie anulujemy. Korekta faktury to osobny proces.',
  emptyDraft: 'Zestawienie nie ma pozycji',
  entryOtherCurrency: 'Wpis jest w innej walucie niż zestawienie',
  alreadyCancelled: 'Zestawienie jest już anulowane',
  notIssued: 'Opłacenie można oznaczyć tylko dla wystawionej faktury',
  alreadyPaid: 'Faktura jest już oznaczona jako opłacona',
  notReopenable: 'Do edycji wraca tylko zestawienie, które czeka na akceptację albo ma błąd',
  notSubmittable: 'Do akceptacji można wysłać tylko zestawienie robocze albo z błędem',
  periodExcludesEntries: 'Zestawienie ma wpisy spoza tego okresu',
  paymentInDraft: 'Płatność jest już w zestawieniu',
  paymentClosed: 'Płatność jest już zafakturowana albo opłacona',
  paymentNoAmount: 'Płatność nie ma kwoty',
  fixedOnlyLine: 'Wpisy czasu usuwasz z listy wpisów, nie jako pozycję',
  invoicingUnavailable: 'System faktur nie jest skonfigurowany',
  draftChanged: 'Zestawienie zmieniło się po wysłaniu do akceptacji. Wyślij je ponownie.',
  issueUnknown:
    'Nie wiadomo, czy faktura powstała (wymaga ręcznej weryfikacji). Sprawdź system fakturowy i potwierdź, że faktury nie ma, zanim zmienisz albo wyślesz zestawienie ponownie.',
  notIssueUnknown: 'Zestawienie nie czeka na ręczną weryfikację wystawienia faktury',
  confirmedNotIssued: 'Sprawdzone ręcznie: faktura nie powstała w systemie fakturowym.',
  issueDateFuture: 'Data wystawienia nie może być w przyszłości',
  issueDateBeforeDraft: 'Data wystawienia jest wcześniejsza niż wysłanie zestawienia do akceptacji',
  invoiceTaken: 'Ta faktura jest już przypisana do innego zestawienia',
  invoicingNotConnected: 'System faktur nie jest podłączony. Połącz go w Ustawieniach → Integracje.',
  // rozliczenia z klientem (workspace bez systemu faktur, DECISIONS 2026-10-09 „Rozliczenia z klientem”)
  settlementOnly: 'Workspace ma podłączony system faktur — wystaw fakturę zamiast oznaczać rozliczenie ręcznie.',
  notSendable: 'Jako wysłane do klienta można oznaczyć tylko szkic rozliczenia',
  sentDateFuture: 'Data wysłania nie może być w przyszłości',
  paidLocked: 'Rozliczenie jest opłacone. Najpierw „Otwórz do edycji”.',
  notPaidSettlement: 'Do edycji otwiera się tylko opłacone rozliczenie z klientem',
  sentChangeInApp:
    'Wysłane rozliczenie zmienia osoba w aplikacji (z uprawnieniem „Wystawia faktury”). AI i konektor zmieniają tylko szkic.',
  invoiceNumberSettlementOnly:
    'Numer faktury wpisuje się ręcznie tylko w rozliczeniach z klientem (bez systemu faktur)',
  sentOnOnlySent: 'Datę wysłania zmienia się tylko w rozliczeniu wysłanym do klienta',
  timeLineInEntries: 'Pozycję czasu zmieniasz we wpisie czasu albo odpinasz ją z listy wpisów',
  // akceptacja wystawienia zamknięta bez wykonania: zestawienie wraca do roboczych (audyt końcowy Fali 6)
  approvalRejected: 'Akceptacja wystawienia odrzucona — zestawienie wróciło do edycji.',
  approvalExpired: 'Akceptacja wystawienia wygasła — zestawienie wróciło do edycji.',
  approvalWithdrawn: 'Akceptacja wystawienia wycofana — zestawienie wróciło do edycji.',
} as const;

/** „Te wpisy są już w zestawieniu ZEST/2026/10/001” (CONFLICT przy drugim rozliczeniu tych samych wpisów). */
export function entriesInDraftMessage(labels: readonly string[]): string {
  const list = [...new Set(labels)];
  if (list.length === 0) return BILLING_MESSAGES.entryInOtherDraft;
  return `Te wpisy są już w zestawieniu ${list.join(', ')}`;
}

// ── Numer i okres ───────────────────────────────────────────────────────────

/**
 * Numer zestawienia do wyświetlania: `ZEST/RRRR/MM/NNN` — rok i miesiąc utworzenia (Europe/Warsaw), NNN = kolejny
 * numer w workspace (`billing_drafts.number` z `workspace_counters`, unikalny w workspace, nie zeruje się co miesiąc).
 */
export function formatBillingDraftNumber(number: number, createdAt: Date | string): string {
  const day = toLocalDate(createdAt instanceof Date ? createdAt : new Date(createdAt));
  return `ZEST/${day.slice(0, 4)}/${day.slice(5, 7)}/${String(number).padStart(3, '0')}`;
}

// ── Wejścia usług (router `billing`) ────────────────────────────────────────

const descriptionSchema = z.string().trim().min(1, { message: 'Opis jest wymagany' }).max(300);

/** Pozycja stała przy tworzeniu zestawienia (np. odnowienie utrzymania). */
export const billingFixedLineSchema = z.object({
  description: descriptionSchema,
  quantity: billingQuantitySchema.default('1'),
  unitPrice: amountSchema,
});
export type BillingFixedLine = z.infer<typeof billingFixedLineSchema>;

/**
 * Nowe zestawienie: klient i okres, wybrane wpisy (`entryIds`) i/lub wszystkie niezafakturowane wpisy z okresu
 * (`allInPeriod`), opcjonalnie pozycje stałe (wymagają „Widzi stawki i kwoty”). Wpis spoza okresu poszerza okres.
 */
export const createBillingDraftInput = billingDraftFieldsBase
  .extend({
    /** domyślnie waluta klienta */
    currency: currencySchema.optional(),
    entryIds: z.array(idSchema).max(1000, { message: 'Najwyżej 1000 wpisów naraz' }).default([]),
    allInPeriod: z.boolean().default(false),
    fixedItems: z.array(billingFixedLineSchema).max(50).default([]),
  })
  .superRefine(billingDraftPeriodRefinement);
export type CreateBillingDraftInput = z.input<typeof createBillingDraftInput>;

export const billingDraftRefInput = z.object({ id: idSchema });

export const billingAddFixedLineInput = billingFixedItemFieldsSchema.extend({ version: versionSchema });

export const billingRemoveLineInput = z.object({ draftId: idSchema, version: versionSchema, itemId: idSchema });

export const updateBillingDraftInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    periodFrom: localDateSchema.optional(),
    periodTo: localDateSchema.optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    /** tryb rozliczeń: numer faktury z programu księgowego (opcjonalny; null = usuń) */
    invoiceNumber: billingInvoiceNumberSchema,
    /** tryb rozliczeń, „Wysłane do klienta”: data wysłania (data rozliczenia w Statystykach) */
    sentOn: localDateSchema.optional(),
  })
  .superRefine(billingDraftPeriodRefinement);

/** Termin płatności faktury w dniach od wystawienia (Figma 09: „termin 14.10”). */
export const BILLING_DUE_DAYS_OPTIONS = [7, 14, 21, 30] as const;
export const DEFAULT_BILLING_DUE_DAYS = 14;

export const submitBillingDraftInput = z.object({
  id: idSchema,
  version: versionSchema,
  dueDays: z.number().int().min(0).max(90).default(DEFAULT_BILLING_DUE_DAYS),
});

export const cancelBillingDraftInput = z.object({
  id: idSchema,
  version: versionSchema,
  reason: z.string().trim().max(500).nullable().optional(),
});

export const reopenBillingDraftInput = z.object({ id: idSchema, version: versionSchema });

// ── Rozliczenia z klientem (workspace bez systemu faktur) ───────────────────

/**
 * Tryb modułu Rozliczenia w workspace (DECISIONS 2026-10-09 [S] „Rozliczenia z klientem”): `invoice` — podłączony
 * realny system faktur (Fakturownia): wystawienie faktury przez akceptację jak dotąd; `settlement` — bez systemu faktur
 * (dostawca `fake` / brak): zestawienia to robocze rozliczenia z klientem (Szkic → Wysłane do klienta → Opłacone;
 * Anulowane), edytowalne do opłacenia, numer faktury opcjonalny i informacyjny.
 */
export const BILLING_MODES = ['invoice', 'settlement'] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export interface BillingModeInfo {
  mode: BillingMode;
  settlement: boolean;
}

/**
 * „Oznacz jako wysłane klientowi” (tryb rozliczeń): szkic → Wysłane do klienta od razu (osoba w aplikacji
 * z „Wystawia faktury”). Data wysłania = data rozliczenia w Statystykach (domyślnie dziś, nie w przyszłości).
 */
export const markBillingDraftSentInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    sentOn: localDateSchema.optional(),
    invoiceNumber: billingInvoiceNumberSchema,
    dueDays: z.number().int().min(0).max(90).default(DEFAULT_BILLING_DUE_DAYS),
  })
  .strict();
export type MarkBillingDraftSentInput = z.input<typeof markBillingDraftSentInput>;

/** „Otwórz do edycji” opłaconego rozliczenia: wraca do „Wysłane do klienta” (data opłacenia usunięta). */
export const reopenPaidBillingDraftInput = z.object({ id: idSchema, version: versionSchema }).strict();

/** Zmiana pozycji stałej (opis, ilość, cena netto) w szkicu albo wysłanym rozliczeniu. */
export const billingUpdateLineInput = z
  .object({
    draftId: idSchema,
    version: versionSchema,
    itemId: idSchema,
    description: z.string().trim().min(1, { message: 'Opis jest wymagany' }).max(300).optional(),
    quantity: billingQuantitySchema.optional(),
    unitPrice: amountSchema.optional(),
  })
  .strict();

/** Stan rozliczenia z klientem (lista, karta, filtr) z `status` i `paidAt`. */
export const BILLING_SETTLEMENT_STATES = ['draft', 'pending', 'sent', 'paid', 'cancelled', 'error'] as const;
export type BillingSettlementState = (typeof BILLING_SETTLEMENT_STATES)[number];

export function billingSettlementState(d: {
  status: BillingDraftStatus;
  paidAt: Date | string | null;
}): BillingSettlementState {
  switch (d.status) {
    case 'issued':
      return d.paidAt ? 'paid' : 'sent';
    case 'pending_approval':
      return 'pending';
    default:
      return d.status;
  }
}

/** „Faktury nie ma w systemie fakturowym” po wystawieniu z nieznanym wynikiem (ręczna weryfikacja). */
export const confirmBillingNotIssuedInput = z.object({ id: idSchema, version: versionSchema });

/**
 * „Oznacz jako wystawione” po wystawieniu z nieznanym wynikiem, gdy faktura JEDNAK powstała (DECISIONS Fala 3,
 * rozstrzygnięcia): numer i data wystawienia przepisane z systemu faktur, opcjonalnie jej identyfikator u dostawcy
 * (wtedy opłacenie może przyjść z synchronizacji `billing.payments.sync`).
 */
export const markBillingDraftIssuedInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    number: z.string().trim().min(1, { message: 'Podaj numer faktury z systemu faktur' }).max(60),
    /** data wystawienia z systemu faktur (Europe/Warsaw), nie w przyszłości */
    issueDate: localDateSchema,
    externalId: z
      .string()
      .trim()
      .max(100)
      .optional()
      .transform((v) => (v ? v : undefined)),
  })
  .strict();
export type MarkBillingDraftIssuedInput = z.input<typeof markBillingDraftIssuedInput>;

/** Zadanie synchronizacji opłaceń z systemu faktur (co godzinę; adapter symulacji = bez zmian). */
export const BILLING_PAYMENTS_SYNC_QUEUE = 'billing.payments.sync';

export const markBillingDraftPaidInput = z.object({
  id: idSchema,
  version: versionSchema,
  /** data opłacenia (Europe/Warsaw); domyślnie dziś */
  paidOn: localDateSchema.optional(),
});

export const billingFromRenewalPaymentInput = z.object({ paymentId: idSchema });

export const listBillingDraftsInput = z
  .object({
    status: billingDraftStatusSchema.optional(),
    clientId: idSchema.optional(),
    /** zestawienia, których okres zachodzi na zakres (daty lokalne, włącznie) */
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    includeCancelled: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(200),
  })
  .default({});

/** Kandydaci do zestawienia: niezafakturowane rozliczane wpisy klienta (w okresie albo wszystkie). */
export const billingCandidatesInput = z
  .object({
    clientId: idSchema,
    periodFrom: localDateSchema.optional(),
    periodTo: localDateSchema.optional(),
  })
  .superRefine(billingDraftPeriodRefinement);

export const billingClientSummaryInput = z.object({ clientId: idSchema });

// ── Wyjścia usług (DTO; bez „Widzi stawki i kwoty” pola z BILLING_MONEY_FIELDS i sumy kwot są pominięte) ──

export interface BillingClientRef {
  id: string;
  name: string;
  color: string | null;
}

export interface BillingInvoiceInfo {
  /** numer nadany przez system faktur, np. „FV/2026/10/001” */
  number: string | null;
  externalId: string | null;
  provider: string | null;
  /** adapter symulacji (fake): faktura nie została wystawiona w systemie zewnętrznym */
  simulated: boolean;
  issuedAt: Date | null;
  dueDate: string | null;
  /** „Oznacz jako wystawione”: numer przepisany ręcznie z systemu faktur (zestawienie odpięte od akceptacji) */
  markedManually?: boolean;
  /**
   * stan w KSeF podany przez system faktur: przy wystawieniu, potem odświeżany przez `billing.payments.sync`
   * (null = brak informacji, symulacja)
   */
  ksef?: BillingKsefState | null;
}

/**
 * KSeF: Fravity OS nie wysyła faktur do KSeF — robi to system faktur. Zapisujemy surowy status dostawcy
 * (Fakturownia `gov_status`) i numer KSeF, a do UI sprowadzamy go do kilku rodzajów.
 */
export const KSEF_STATUS_KINDS = [
  'ok',
  'pending',
  'error',
  'offline',
  'not_applicable',
  'not_connected',
  'unknown',
] as const;
export type KsefStatusKind = (typeof KSEF_STATUS_KINDS)[number];

export interface BillingKsefState {
  /** surowy status dostawcy, np. `ok`, `processing`, `send_error`, `demo_ok` */
  status: string;
  kind: KsefStatusKind;
  /** tryb demo / testowy KSeF (przedrostek `demo_`) */
  demo: boolean;
  /** numer KSeF faktury */
  number: string | null;
  /** komunikaty błędów KSeF z systemu faktur (np. walidacja danych nabywcy; najwyżej 5, bez danych dostępowych) */
  errors: string[];
  /** ostatnie sprawdzenie w systemie faktur (null = tylko odpowiedź przy wystawieniu) */
  checkedAt: Date | null;
}

/**
 * Stan KSeF zapisany w `billing_drafts.ksef` (jsonb): surowe pola z systemu faktur i chwila sprawdzenia (ISO).
 * Zapisują go: wystawienie (odpowiedź na utworzenie faktury) i synchronizacja `billing.payments.sync`.
 */
export interface BillingKsefStored {
  status: string;
  number: string | null;
  errors?: string[];
  /** ISO 8601 */
  checkedAt?: string | null;
}

/** Statusy KSeF, które jeszcze się zmienią (sprawdzamy je przy synchronizacji także po opłaceniu faktury). */
export const KSEF_TRANSIENT_STATUSES = ['processing', 'offline', 'server_error', 'status_check_error'] as const;

/** Czy status KSeF (także `demo_…`) może się jeszcze zmienić bez działania osoby. */
export function isKsefTransient(raw: string | null | undefined): boolean {
  const s = (raw ?? '').replace(/^demo_/, '');
  return (KSEF_TRANSIENT_STATUSES as readonly string[]).includes(s);
}

/** Ten sam stan KSeF (status, numer, błędy) — bez zapisu, gdy nic się nie zmieniło. */
export function sameKsef(
  a: { status: string; number?: string | null; errors?: string[] } | null | undefined,
  b: { status: string; number?: string | null; errors?: string[] } | null | undefined,
): boolean {
  if (!a || !b) return !a && !b;
  return (
    a.status === b.status &&
    (a.number ?? null) === (b.number ?? null) &&
    JSON.stringify(a.errors ?? []) === JSON.stringify(b.errors ?? [])
  );
}

/** Rodzaj statusu KSeF z surowej wartości dostawcy (Fakturownia KSeF.md; przedrostek `demo_` = środowisko testowe). */
export function ksefStatusKind(raw: string | null | undefined): KsefStatusKind {
  const s = (raw ?? '').replace(/^demo_/, '');
  if (s === 'ok') return 'ok';
  if (s === 'processing') return 'pending';
  if (s === 'offline') return 'offline';
  if (s === 'not_applicable') return 'not_applicable';
  if (s === 'not_connected') return 'not_connected';
  if (/error$/.test(s)) return 'error';
  return 'unknown';
}

export function ksefState(
  ksef: { status: string; number?: string | null; errors?: string[]; checkedAt?: string | null } | null | undefined,
): BillingKsefState | null {
  if (!ksef?.status) return null;
  const checkedAt = ksef.checkedAt ? new Date(ksef.checkedAt) : null;
  return {
    status: ksef.status,
    kind: ksefStatusKind(ksef.status),
    demo: ksef.status.startsWith('demo_'),
    number: ksef.number ?? null,
    errors: (ksef.errors ?? []).filter((e) => typeof e === 'string').slice(0, 5),
    checkedAt: checkedAt && !Number.isNaN(checkedAt.getTime()) ? checkedAt : null,
  };
}

export interface BillingDraftListItem {
  id: string;
  version: number;
  number: number;
  /** ZEST/2026/10/001 */
  label: string;
  client: BillingClientRef;
  periodFrom: string;
  periodTo: string;
  status: BillingDraftStatus;
  currency: string;
  totalSeconds: number;
  /** tylko z „Widzi stawki i kwoty” */
  totalNet?: Money;
  invoice: BillingInvoiceInfo | null;
  /**
   * numer faktury: z systemu faktur (jak `invoice.number`) albo — w rozliczeniu z klientem — wpisany ręcznie,
   * informacyjny (także w szkicu); null = brak
   */
  invoiceNumber: string | null;
  paidAt: Date | null;
  createdAt: Date;
}

export interface BillingDraftEntryInfo {
  startedAt: Date;
  endedAt: Date;
  userId: string;
  userName: string | null;
  projectId: string | null;
  projectName: string | null;
}

export interface BillingDraftItemDto {
  id: string;
  kind: BillingItemKind;
  description: string;
  position: string;
  timeEntryId: string | null;
  quantitySeconds: number | null;
  quantity: string | null;
  /** stawka (czas) albo cena jednostkowa (pozycja stała); tylko z „Widzi stawki i kwoty” */
  unitPrice?: Money | null;
  amount?: Money;
  /** wpis czasu (data, godziny, osoba, projekt) — tylko pozycje `time` */
  entry: BillingDraftEntryInfo | null;
}

export interface BillingDraftDto extends BillingDraftListItem {
  notes: string | null;
  items: BillingDraftItemDto[];
  timeSeconds: number;
  /**
   * sumy części czasowej i stałej, różne stawki pozycji czasu, pozycje wg stawki (stawka × łączny czas, kwota
   * zaokrąglona raz; `timeNet` = suma `rateLines`) — tylko z „Widzi stawki i kwoty”
   */
  timeNet?: Money;
  fixedNet?: Money;
  rates?: Money[];
  rateLines?: RateLine[];
  /** wpisy rozliczane bez stawki (kwota 0 zł) — ostrzeżenie w UI */
  entriesWithoutRate: number;
  projects: string[];
  ownerName: string | null;
  /** akceptacja wystawienia faktury: czeka / wykonywana / wykonana (symulacja) / błąd / wycofana (kompozycja Fali 3) */
  approval: ApprovalStateRef | null;
  lastError: string | null;
  cancelledAt: Date | null;
  paymentSource: BillingPaymentSource | null;
  renewalPayments: Array<{ id: string; serviceId: string; periodStart: string; periodEnd: string; status: string }>;
  createdBy: string | null;
}

export interface BillingCandidateEntry {
  id: string;
  date: string;
  startedAt: Date;
  endedAt: Date;
  durationSeconds: number;
  description: string;
  userId: string;
  userName: string | null;
  projectId: string | null;
  projectName: string | null;
  rate?: Money | null;
  amount?: Money;
}

export interface BillingCandidateGroup {
  project: { id: string; name: string } | null;
  seconds: number;
  amount?: Money;
  entries: BillingCandidateEntry[];
}

export interface BillingCandidates {
  client: BillingClientRef;
  /** waluta klienta (nowe zestawienie dziedziczy ją; wpisy w innej walucie nie dadzą się dołączyć) */
  currency: string;
  periodFrom: string | null;
  periodTo: string | null;
  count: number;
  totalSeconds: number;
  /** suma kwot wpisów w walucie `currency` (wpisy w innych walutach pominięte) */
  totalAmount?: Money;
  /** niezafakturowane wpisy klienta poza okresem (podpowiedź „Dodaj wpisy”) */
  outsidePeriod: number;
  groups: BillingCandidateGroup[];
}

/** Niezafakturowany klient: kwota w `currency`, inne waluty w `otherAmounts` (tylko z „Widzi stawki i kwoty”). */
export interface BillingUnbilledClient extends Partial<MoneyTotal> {
  client: BillingClientRef;
  seconds: number;
  entries: number;
  firstDate: string;
  lastDate: string;
  /** wpisy rozliczane bez stawki (kwota 0 zł) */
  withoutRate: number;
}

/**
 * Kafelki Rozliczeń. Kwoty (tylko z „Widzi stawki i kwoty”) per waluta: `amount` w `currency` (PLN, gdy występuje),
 * pozostałe waluty w `otherAmounts` — kwot w różnych walutach nie sumujemy.
 */
export interface BillingOverview {
  unbilled: { seconds: number; entries: number; clients: number } & Partial<MoneyTotal>;
  pending: {
    count: number;
    first: { clientName: string; periodFrom: string; periodTo: string } | null;
  } & Partial<MoneyTotal>;
  toPay: { count: number; nearestDueDate: string | null; overdue: number } & Partial<MoneyTotal>;
  paidThisMonth: { count: number; month: string } & Partial<MoneyTotal>;
  toBill: BillingUnbilledClient[];
}

export interface BillingClientSummary {
  clientId: string;
  /** kwota w `currency`, inne waluty w `otherAmounts` (tylko z „Widzi stawki i kwoty”) */
  unbilled: {
    seconds: number;
    entries: number;
    firstDate: string | null;
    lastDate: string | null;
  } & Partial<MoneyTotal>;
  openDrafts: number;
  /** ostatnie nieanulowane zestawienie (null bez modułu Rozliczenia albo gdy nie ma) */
  lastDraft: BillingDraftListItem | null;
}

/** Eksport zestawienia (REST `/api/exports/billing/<id>.csv|pdf`; flaga „Eksportuje dane”). */
export const BILLING_EXPORT_FORMATS = ['csv', 'pdf'] as const;
export type BillingExportFormat = (typeof BILLING_EXPORT_FORMATS)[number];

export const billingExportInput = z.object({
  id: idSchema,
  format: z.enum(BILLING_EXPORT_FORMATS).default('csv'),
});

/**
 * Model eksportu zestawienia (CSV i PDF budują z niego adaptery w apps/api). Bez „Widzi stawki i kwoty”
 * `draft` nie ma pól pieniężnych, a `showMoney = false` (CSV i PDF bez kolumn kwot).
 */
export interface BillingExportModel {
  draft: BillingDraftDto;
  showMoney: boolean;
  /**
   * rozliczenie z klientem (workspace bez systemu faktur, DECISIONS 2026-10-09): statusy Szkic / Wysłane do klienta /
   * Opłacone, „Numer faktury” zamiast „Faktura”, bez not o fakturze (brak = faktura, jak dotąd)
   */
  settlement?: boolean;
  generatedAt: Date;
  /** nazwa pliku bez rozszerzenia: `zestawienie-ZEST-2026-10-001-helpmycar` */
  baseFilename: string;
  workspace: { name: string; slug: string; website: string | null; contact: string | null; ownerName: string | null };
}

// ── Akceptacja wystawienia faktury ──────────────────────────────────────────

/** Typ akcji akceptacji: wystawienie faktury z zestawienia (wymaga „Wystawia faktury”). */
export const BILLING_ISSUE_INVOICE = 'billing.issue_invoice';

/** Stawki VAT pozycji faktury (jak w adapterze faktur @fos/integrations/invoicing). */
export const INVOICE_VAT_RATES = ['23', '8', '5', '0', 'zw', 'np'] as const;
export type InvoiceVatRate = (typeof INVOICE_VAT_RATES)[number];

/** Domyślna stawka VAT pozycji faktury (VAT i tak liczy system faktur; Fravity jest czynnym podatnikiem). */
export const DEFAULT_INVOICE_VAT_RATE: InvoiceVatRate = '23';

export const billingInvoiceLineSchema = z.object({
  kind: billingItemKindSchema,
  description: z.string().min(1).max(500),
  /** ilość dziesiętna jako tekst ("1", "2.5") */
  quantity: z.string().regex(/^\d{1,10}(\.\d{1,4})?$/),
  unit: z.string().min(1).max(20),
  unitPrice: amountSchema,
  amount: amountSchema,
  vatRate: z.enum(INVOICE_VAT_RATES),
  /** czas pozycji (sekundy) — pozycje `time` */
  seconds: z.number().int().min(0).nullable(),
});
export type BillingInvoiceLine = z.infer<typeof billingInvoiceLineSchema>;

/**
 * Payload akceptacji `billing.issue_invoice` (MVP §10: faktura pokazuje klienta, pozycje, kwoty, walutę i termin).
 * Pozycje = dokładnie to, co trafi do systemu faktur: czas zgrupowany po stawce w jedną pozycję („Usługi wg zestawienia
 * … (29 h 00 min × 180,00 zł/h)”, ilość 1 × round(stawka × łączny czas, 2) — `computeDraftRateLines`), pozycje stałe
 * bez zmian — suma netto faktury = suma zestawienia.
 */
export const billingIssueInvoicePayloadSchema = z.object({
  draftId: idSchema,
  draftNumber: z.string().min(1).max(40),
  /**
   * Nabywca z karty klienta: nazwa wyświetlana, NIP, a od Fali 7 nazwa prawna i adres siedziby (dane firmy, także
   * z rejestru po NIP). Starsze akceptacje nie mają `legalName` / `address` — faktura dostaje wtedy samą nazwę.
   */
  client: z.object({
    id: idSchema,
    name: z.string().min(1),
    nip: z.string().nullable(),
    legalName: z.string().min(1).max(500).nullable().optional(),
    address: companyAddressSchema.nullable().optional(),
  }),
  period: z.object({ from: localDateSchema, to: localDateSchema }),
  lines: z.array(billingInvoiceLineSchema).min(1),
  totals: z.object({ net: amountSchema, seconds: z.number().int().min(0) }),
  currency: currencySchema,
  dueDays: z.number().int().min(0).max(90),
  /**
   * rozliczenie z klientem (workspace bez systemu faktur, DECISIONS 2026-10-09): zatwierdzenie oznacza zestawienie jako
   * wysłane klientowi — bez faktury i bez adaptera faktur. Brak = faktura (starsze akceptacje, podłączony system faktur).
   */
  settlement: z.boolean().optional(),
});
export type BillingIssueInvoicePayload = z.infer<typeof billingIssueInvoicePayloadSchema>;

/** Wynik wykonania (zapisywany w `approvals.result`). */
export interface BillingIssueInvoiceResult {
  draftId: string;
  number: string;
  externalId: string;
  provider: string;
  issuedAt: Date;
  dueDate: string;
  simulated: boolean;
  /** status KSeF z odpowiedzi systemu faktur (Fala 4; brak = symulacja albo dostawca go nie podał) */
  ksef?: { status: string; number: string | null } | null;
  /** sumy netto faktury u dostawcy różnią się od zestawienia (zaokrąglenia — do sprawdzenia w systemie faktur) */
  totalsMismatch?: boolean;
}

// ── Statystyki (Rozliczenia → Statystyki, prośba Sebastiana 9.10.2026) ──────

/**
 * Statystyki przychodu (`billing.stats`): moduł Rozliczenia (podgląd) + „Widzi stawki i kwoty”, zakres klientów osoby.
 * Przychód netto = zestawienia wystawione (`issued`, także „Oznacz jako wystawione”; w rozliczeniach z klientem
 * „Wysłane do klienta” + „Opłacone”) wg daty wystawienia / wysłania w Europe/Warsaw; opłacone = te z datą opłacenia;
 * czeka na zapłatę = wystawione / wysłane, nieopłacone (netto = opłacone + czeka). Kwoty = bieżące sumy zestawień
 * (po edycji wysłanego rozliczenia — nowe). Kwoty tylko w PLN — zestawienia w innych walutach są osobno w `otherCurrencies` (bez przeliczania).
 * `month` (1–12) wybiera miesiąc rozbicia; bez niego: bieżący miesiąc w bieżącym roku, w innym roku ostatni
 * miesiąc z przychodem (albo grudzień).
 */
export const billingStatsInput = z
  .object({
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12).optional(),
  })
  .strict();
export type BillingStatsInput = z.input<typeof billingStatsInput>;

export interface BillingStatsMonth {
  /** 1–12 */
  month: number;
  net: Money;
  paid: Money;
  /** liczba wystawionych zestawień (faktur) */
  invoices: number;
  /** czas z pozycji `time` wystawionych zestawień (sekundy) */
  seconds: number;
  /** netto tego miesiąca rok wcześniej */
  prevNet: Money;
}

/** Linia rozbicia klienta: projekt, czas bez projektu albo pozycje stałe. */
export interface BillingStatsLine {
  kind: 'project' | 'no_project' | 'fixed';
  project: { id: string; name: string } | null;
  net: Money;
  seconds: number;
}

export interface BillingStatsClient {
  client: BillingClientRef;
  net: Money;
  /** udział w miesiącu (albo roku), procent z jednym miejscem po przecinku */
  share: number;
  invoices: number;
  seconds: number;
}

export interface BillingStatsBreakdownClient extends BillingStatsClient {
  lines: BillingStatsLine[];
}

export interface BillingStats {
  year: number;
  currency: 'PLN';
  /** lata z wystawionymi zestawieniami (w zakresie osoby) i bieżący rok, rosnąco */
  years: number[];
  totals: {
    net: Money;
    paid: Money;
    unpaid: Money;
    unpaidCount: number;
    invoices: number;
    seconds: number;
    /** netto całego poprzedniego roku */
    prevNet: Money;
    /** niezafakturowany czas z datą w tym roku (rozliczany, bez zestawienia), wyceniony stawką z wpisu */
    unbilled: Money;
    unbilledSeconds: number;
  };
  months: BillingStatsMonth[];
  /** top 5 klientów w roku wg netto */
  topClients: BillingStatsClient[];
  /** „Z czego składa się miesiąc” */
  breakdown: { month: number; net: Money; clients: BillingStatsBreakdownClient[] };
  /** wystawione w roku zestawienia w innych walutach niż PLN (nie wliczone w sumy) */
  otherCurrencies: Array<{ currency: string; amount: Money }>;
}
